<script lang="ts">
  import { Text, AccordionToggle } from '@squareup/public-web-design-system';
  import { onDestroy } from 'svelte';

  export let title: string;
  export let reducedMotion = false;

  let detailsEl: HTMLDetailsElement;
  let contentEl: HTMLElement;
  let isClosing = false;
  let isExpanding = false;
  let isOpen: boolean = false;
  let animation: Animation | null = null;
  let animationFrameId: number | null = null;

  $: if (detailsEl) {
    const shouldOpen = isOpen && !detailsEl.open && !isExpanding && !isClosing;
    const shouldClose = !isOpen && detailsEl.open && !isExpanding && !isClosing;

    if (shouldOpen) {
      openAccordion();
    } else if (shouldClose) {
      shrinkAccordion();
    }
  }

  function handleToggle(e: Event) {
    e.preventDefault();
    isOpen = !isOpen;
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (['Enter', ' ', 'Space', 'Spacebar'].includes(e.key)) {
      e.preventDefault();
      handleToggle(e);
    }
  }

  function animateAccordion(startHeight: string, endHeight: string, onFinish: () => void) {
    if (reducedMotion) {
      onFinish();
      return;
    }

    if (animation) {
      animation.cancel();
    }

    animation = detailsEl.animate(
      { height: [startHeight, endHeight] },
      {
        duration: 300,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
      }
    );

    animation.onfinish = () => {
      onFinish();
    };
    animation.oncancel = () => {
      isClosing = false;
      isExpanding = false;
    };
  }

  function shrinkAccordion() {
    if (isClosing || isExpanding) {
      return;
    }

    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    isClosing = true;
    contentEl.style.opacity = '0';
    animateAccordion(`${detailsEl.offsetHeight}px`, `${detailsEl.offsetHeight - contentEl.offsetHeight}px`, () =>
      onAnimationFinish(false)
    );
  }

  function openAccordion() {
    if (isClosing || isExpanding) {
      return;
    }
    isExpanding = true;
    detailsEl.style.height = `${detailsEl.offsetHeight}px`;
    detailsEl.open = true;

    animationFrameId = requestAnimationFrame(() => {
      contentEl.style.opacity = '0';
      animateAccordion(`${detailsEl.offsetHeight}px`, `${detailsEl.offsetHeight + contentEl.offsetHeight}px`, () => {
        onAnimationFinish(true);
      });

      if (reducedMotion) {
        contentEl.style.opacity = '1';
      } else {
        setTimeout(() => {
          contentEl.style.opacity = '1';
        }, 150);
      }
    });
  }

  function onAnimationFinish(open: boolean) {
    detailsEl.open = open;
    animation = null;
    isClosing = false;
    isExpanding = false;
    detailsEl.style.height = '';
    detailsEl.style.overflow = '';
  }

  onDestroy(() => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
    if (animation) {
      animation.cancel();
      animation = null;
    }
  });
</script>

<details class="AccordionItem" bind:this={detailsEl}>
  <summary
    class="AccordionItem__summary"
    tabindex="0"
    class:disabled={isExpanding || isClosing}
    aria-expanded={detailsEl ? detailsEl.open : false}
    on:click={handleToggle}
    on:keydown={handleKeyDown}
  >
    <Text variant="100">{title}</Text>
    <AccordionToggle isToggled={isOpen} />
  </summary>
  <div class="AccordionItem__content" bind:this={contentEl} role="region">
    <slot />
  </div>
</details>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/_media_queries' as mediaQueries;

  @layer custom-components {
    .AccordionItem {
      display: none;

      @include mediaQueries.until('medium') {
        display: block;
      }

      &__summary {
        appearance: none;
        position: relative;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        list-style: none;

        &::-webkit-details-marker {
          display: none;
        }

        &::marker {
          content: '';
          display: none;
        }
      }

      &__content {
        opacity: 0;
        padding-top: var(--space-20);
        transition: opacity var(--duration-quick) var(--easing-standard);
        display: flex;
        flex-direction: column;
        gap: var(--space-20);
      }
    }
  }
</style>
